<script setup lang="ts">
import type { Address } from 'viem';

import { computed, useAlertsStore, useEventModalsStore, useUserStore } from '#imports';
import { useOfferColumns } from '~/components/widgets/info-token/Offers/useOfferColumns';
import type { EventModalCustomError } from '~/components/widgets/modals/EventModal/EventModal.types';
import type { OfferListParams } from '~/service/api/offer/types';

const { offer, QueryEvmKey } = useEVMApi();

const props = defineProps<{
    collectionAddress?: Address;
    identifier?: string;
}>();

const userStore = useUserStore();

type RouteStateType = OfferListParams &
    Pick<ApiPagination, 'page' | 'size'>;

const params = ref<RouteStateType>({
    page: 1,
    size: 4,
    sellerAddress: undefined,
    direction: 'asc',
    sort: 'endTime'
});

const total = ref(0);

const {
    data: offers,
    status: statusOffers,
    refresh
} = useAsyncData(`${props.collectionAddress}-${props.identifier}-${QueryEvmKey.OFFER_LIST}`, async () => {
    if (props.collectionAddress && props.identifier) {
        const { data, pagination } = await offer.listOffer({
            ...params.value,
            collectionAddress: props.collectionAddress,
            identifier: props.identifier
        });
        total.value = pagination.total;
        return data;
    }
    return [];
}, {
    default: () => [],
    watch: [params]
});

const onChangeQueryParams = (values: Partial<RouteStateType>) => {
    params.value = { ...params.value, ...values };
};

const onChangeSort = (sort: RouteStateType['sort']) => {
    if (params.value.direction === 'asc' && params.value.sort === sort) {
        params.value = { ...params.value, direction: 'desc' };
    }
    else {
        params.value = { ...params.value, sort, direction: 'asc' };
    }
};

const showFilterByMyOffers = computed(() => userStore.user ? params.value.sellerAddress === userStore.user : false);

const columns = useOfferColumns(field => onChangeSort(field as RouteStateType['sort']));

const modalEventsStore = useEventModalsStore();
const alertStore = useAlertsStore();

const { eventEmitter } = useEVMCore();

onMounted(() => {
    eventEmitter.on('sendCancel', {
        key: 'CommonInfoTokenOffers',
        onSuccess: () => {
            modalEventsStore.removeByType('cancel_offer');
            alertStore.add({
                critical: false,
                description: ``,
                status: 'type.success',
                title: 'Offer cancelled successfully'
            });
            refresh();
        },
        onError: (error) => {
            const typedError = error as EventModalCustomError;
            const { modalInfo } = typedError;
            modalEventsStore.add(
                {
                    type: 'error',
                    status: 'error',
                    images: modalInfo.images,
                    imageState: 'error',
                    title: modalInfo.title,
                    description: modalInfo.description,
                    heading: 'Error',
                    itemType: modalInfo.itemType,
                    content: 'Something went wrong, Try again later.'

                });
        },
        onLoad: (loading) => {
            if (loading) {
                console.log('sendCancel onLoad ', loading);
            }
        }
    });
});

onUnmounted(() => {
    eventEmitter.off('sendCancel', 'CommonInfoTokenOffers');
});

</script>

<template>
  <div class="grow display-flex flex-column">
    <div v-if="userStore.user && offers?.length"
         class="display-flex flex-row align-items-center filter-table">
      <span>My offers</span>
      <Checkbox :checked="showFilterByMyOffers"
                class="ml-8"
                @on-change="(checked: boolean) => onChangeQueryParams({ sellerAddress: checked && userStore.user ? userStore.user : undefined, page: 1 })"
      />
    </div>

    <Table :columns="columns"
           class="grow table"
           :is-loading="statusOffers === 'pending'"
           :data="offers"
           :skeleton="{ rows: 4, height: 18 }">
      <template #no-data>
        <div class="display-flex flex-column align-items-center justify-center gap-12 pad-center">
          <div class="empty-circle display-flex align-items-center justify-center">
            <IconMain icon="air"
                      :size="28"
            />
          </div>
          <div class="display-flex flex-column align-items-center justify-center text-color-primary">
            <p class="compact-500-accent">
              No offers
            </p>
            <p class="text-400-default color-text-grey">
              Make your offer
            </p>
          </div>
        </div>
      </template>
    </Table>
    <Suspense>
      <LazyPagination :page="params.page"
                      :size="params.size"
                      :total="total"
                      :visible-items="3"
                      @on-page="(page: number) => onChangeQueryParams({ page })"
      />
      <template #fallback>
        <div class="display-flex flex-row align-items-center paginate gap-8">
          <LoaderSkeleton :height="30"
                          :width="30" />
          <LoaderSkeleton :height="30"
                          :width="30" />
          <LoaderSkeleton :height="30"
                          :width="30" />
        </div>
      </template>
    </Suspense>
  </div>
</template>

<style lang="scss" scoped>

.filter-table {
  align-self: end;
}

:deep(.logo) {
  width: 24px;
  height: 24px;
}

.empty-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--bg-secondary, #3F3F47);
}

.desktop-table {
  display: table;
}

.mobile-table {
  display: none;
}

table {
  width: 100%;
  border-collapse: separate;
}

th, td {
  width: calc(100% / 5);
  padding-bottom: 8px;
  vertical-align: middle;

  &:not(:last-child) {
    padding-right: 8px;
  }
}

.head-cell {
  height: 40px;
}

.table-row {
  margin-bottom: 16px;
}

@include mobile-576 {
  .desktop-table {
    display: none;
  }

  .mobile-table {
    display: table;
  }
}

</style>
